import { useCallback, useEffect, useRef, useState } from 'react';
import {
    getMultipartUploadPartUrl,
    initiateMultipartUpload,
} from '../requests';
import { appServer } from '../api';
import {
    MAX_PARALLEL_UPLOADS,
    PRESIGNED_URL_BATCH_SIZE,
    PRESIGNED_URL_REFILL_THRESHOLD,
} from './MultipartUploadConstants';
import {
    MultipartUploadAbortReason,
    GetMultipartUploadPartUrlResponse,
    InitialUpload,
    InitiateMultipartUploadResponse,
    ReadyUpload,
    ReadyUploadChunk,
    Upload,
    UploadChunk,
} from './useMultipartUploadTypes';
import {
    abortMultipartUploadRequest,
    buildUploadUrl,
    chunkIndexToPartNumber,
    concludeMultipartUploadRequest,
    doesChunkNeedPresignedUrl,
    hasChunkUploadFailed,
    hasUploadConcludeFailed,
    isChunkEligibleForUpload,
    isReadyUpload,
    isUploadReadyToConclude,
    moveChunksToNewState,
    uploadPart,
    validateConcludeResponse,
} from './useMultipartUploadUtils';
import logger from '@upheal/utils/logger';
import { useThrottledCallback } from 'use-debounce';
import { captureException } from '../sentry';

export const useMultipartUpload = () => {
    // Keeps all chunks from start until the parent upload is removed
    const allChunksPerUpload = useRef<
        Record<Upload['id'], Array<UploadChunk> | undefined>
    >({});
    // For keeping track of number of chunks that are being uploaded in parallel
    const parallelUploadingChunks = useRef<Set<UploadChunk>>(new Set());
    const [uploadQueue, setUploadQueue] = useState<Array<Upload>>([]);
    const [shouldTryChunkUpload, setShouldTryChunkUpload] = useState(0);
    const currentlyPreparedUploads = useRef<Set<string>>(new Set());
    const currentlyConcludingUploads = useRef<Set<string>>(new Set());
    const fetchingPresignedUrls = useRef(false);

    const addChunks = useCallback(
        (uploadId: Upload['id'], newChunks: Array<UploadChunk>) => {
            const allChunks = allChunksPerUpload.current[uploadId];

            if (allChunks) {
                allChunks.push(...newChunks);
            } else {
                logger.warn(
                    '[multipart-upload] addChunks called before enqueue upload',
                    {
                        uploadId: uploadId,
                    },
                );
            }

            setShouldTryChunkUpload((prev) => prev + 1);
        },
        [setShouldTryChunkUpload],
    );

    const enqueueUpload = useCallback(
        (upload: InitialUpload) => {
            // Use ref to track enqueued uploads synchronously, preventing duplicates
            // even when called rapidly before React state updates. This is necessary
            // because allChunksPerUpload must be initialized synchronously for addChunks
            // to work correctly when called immediately after enqueueUpload.
            if (allChunksPerUpload.current[upload.id]) {
                captureException(
                    `[multipart-upload] attempted to enqueue upload already in queue.`,
                    {
                        uploadId: upload.id,
                        inputFileSource: upload.inputFileSource,
                    },
                );
                return;
            }

            allChunksPerUpload.current[upload.id] = [];
            upload.onStateChange('init');

            logger.debug('[multipart-upload] enqueue upload', {
                uploadId: upload.id,
                inputFileSource: upload.inputFileSource,
            });

            // Using a functional updater ensures we always see the latest queue state
            setUploadQueue((prevQueue) => {
                const newUpload = {
                    ...upload,
                    uploadedChunksCount: 0,
                    state: 'init' as const,
                    parts: [],
                    concludeAttempt: 0,
                };

                return upload.shouldImmediatelyStart
                    ? [newUpload, ...prevQueue]
                    : [...prevQueue, newUpload];
            });
        },
        [setUploadQueue],
    );

    const abortUploads = useCallback(
        (uploadIds: Array<string>, reason: MultipartUploadAbortReason) => {
            uploadIds.forEach((uploadId) => {
                const uploadToRemove = uploadQueue.find(
                    (upload) => upload.id === uploadId,
                );

                if (!uploadToRemove) {
                    return;
                }

                logger.debug('[multipart-upload] removing upload from queue', {
                    uploadId: uploadToRemove.id,
                    inputFileSource: uploadToRemove.inputFileSource,
                    reason,
                });

                // If the upload has this URl, we want to also abort the upload on AWS
                if (uploadToRemove.abortPresignedUrl) {
                    abortMultipartUploadRequest({
                        abortPresignedUrl: uploadToRemove.abortPresignedUrl,
                    })
                        .then(() => {
                            logger.debug(
                                '[multipart-upload] aborted upload on aws',
                                {
                                    uploadId: uploadToRemove.id,
                                    inputFileSource:
                                        uploadToRemove.inputFileSource,
                                    reason,
                                },
                            );
                        })
                        .catch((error) => {
                            captureException(
                                '[multipart-upload] failed to abort multipart upload on AWS.',
                                {
                                    error,
                                    uploadId: uploadToRemove.id,
                                    inputFileSource:
                                        uploadToRemove.inputFileSource,
                                },
                            );
                        });
                }

                const uploadingChunks = allChunksPerUpload.current[
                    uploadId
                ]?.filter((chunk) => chunk.state !== 'done');

                if (uploadingChunks) {
                    uploadingChunks.forEach((chunk) =>
                        parallelUploadingChunks.current.delete(chunk),
                    );
                }
                uploadToRemove.onAbort(reason);
                uploadToRemove.onStateChange('aborted');

                if (reason !== 'manualRemoval') {
                    captureException(
                        '[multipart-upload] aborting multipart upload.',
                        {
                            abortReason: reason,
                            uploadId: uploadToRemove.id,
                            inputFileSource: uploadToRemove.inputFileSource,
                        },
                    );
                }

                delete allChunksPerUpload.current[uploadId];

                setUploadQueue((prevQueue) =>
                    prevQueue.filter((upload) => upload.id !== uploadId),
                );
            });
        },
        [setUploadQueue, uploadQueue],
    );

    const fetchPresignedUrlForChunks = useCallback(
        async (chunks: Array<UploadChunk>, upload: ReadyUpload) => {
            moveChunksToNewState(chunks, 'fetchingUrl');

            try {
                fetchingPresignedUrls.current = true;

                const parts = chunks.map((chunk) => ({
                    partNumber: chunkIndexToPartNumber(chunk.chunkIndex),
                }));

                const requestUploadUrl = getMultipartUploadPartUrl(
                    upload.routePrefix,
                    {
                        uploadId: upload.aws.uploadId,
                        bucket: upload.aws.bucket,
                        key: upload.aws.key,
                        parts,
                    },
                );

                const response =
                    await appServer.request<GetMultipartUploadPartUrlResponse>(
                        requestUploadUrl,
                    );

                chunks.forEach((chunk) => {
                    const responseWithUrl = response.data.parts.find(
                        (responseChunk) =>
                            responseChunk.partNumber ===
                            chunkIndexToPartNumber(chunk.chunkIndex),
                    );

                    if (responseWithUrl) {
                        chunk.presignedUrl = responseWithUrl.presignedUrl;
                    } else {
                        logger.debug(
                            '[multipart-upload] presigned part upload URL was not found in response',
                            {
                                uploadId: upload.id,
                                inputFileSource: upload.inputFileSource,
                                affectedChunkIndex: chunk.chunkIndex,
                                numberOfChunks: chunks.length,
                            },
                        );
                    }
                });

                logger.debug('[multipart-upload] fetched chunk upload urls', {
                    uploadId: upload.id,
                    inputFileSource: upload.inputFileSource,
                    numberOfChunks: chunks.length,
                });
            } catch (error) {
                captureException(
                    '[multipart-upload] failed to get presigned URLs for chunks.',
                    {
                        error,
                        uploadId: upload.id,
                        inputFileSource: upload.inputFileSource,
                    },
                );

                // Abort the upload if we fail to get presigned URLs. I'm not really sure how to handle retries in this case.
                abortUploads([upload.id], 'failedToGetPresignedPartUrls');
            } finally {
                moveChunksToNewState(chunks, 'idle');
                fetchingPresignedUrls.current = false;
                setShouldTryChunkUpload((prev) => prev + 1);
            }
        },
        [abortUploads, setShouldTryChunkUpload],
    );

    const concludeMultipartUpload = useCallback(
        async (upload: ReadyUpload) => {
            if (currentlyConcludingUploads.current.has(upload.id)) {
                return;
            }

            if (hasUploadConcludeFailed(upload)) {
                abortUploads([upload.id], 'failedToConcludeUpload');

                return;
            }

            setUploadQueue((prevQueue) => {
                return prevQueue.map((queuedUpload) => {
                    if (queuedUpload.id === upload.id) {
                        return {
                            ...queuedUpload,
                            state: 'concludingMultipartUpload',
                        };
                    }

                    return queuedUpload;
                });
            });
            upload.onStateChange('concludingMultipartUpload');

            const concludePayload = {
                completePresignedUrl: upload.completePresignedUrl,
                parts: upload.parts.sort((a, b) => a.partNumber - b.partNumber),
            };

            logger.debug(
                '[multipart-upload] conclude payload object',
                concludePayload,
            );

            try {
                const response = await concludeMultipartUploadRequest(
                    concludePayload,
                );

                validateConcludeResponse(response, upload.id);

                upload.onProgress(1, 'done');
                upload.onStateChange('done');

                setUploadQueue((prevQueue) =>
                    prevQueue.filter(
                        (queuedUpload) => queuedUpload.id !== upload.id,
                    ),
                );

                delete allChunksPerUpload.current[upload.id];

                logger.debug('[multipart-upload] completed upload on aws', {
                    uploadId: upload.id,
                    inputFileSource: upload.inputFileSource,
                });

                setShouldTryChunkUpload(0);
            } catch (error) {
                captureException(
                    '[multipart-upload] failed to conclude multipart upload on AWS.',
                    {
                        error,
                        uploadId: upload.id,
                        inputFileSource: upload.inputFileSource,
                        attempt: upload.concludeAttempt,
                    },
                );

                setUploadQueue((prevQueue) => {
                    return prevQueue.map((queuedUpload) => {
                        if (queuedUpload.id === upload.id) {
                            return {
                                ...queuedUpload,
                                concludeAttempt: upload.concludeAttempt + 1,
                                state: 'finishedWithCurrentChunks',
                            };
                        }

                        return queuedUpload;
                    });
                });
            } finally {
                currentlyConcludingUploads.current.delete(upload.id);
            }
        },
        [abortUploads, setShouldTryChunkUpload, setUploadQueue],
    );

    const prepareMultipartUpload = useCallback(
        async (upload: Upload) => {
            if (currentlyPreparedUploads.current.has(upload.id)) {
                return;
            }

            currentlyPreparedUploads.current.add(upload.id);

            setUploadQueue((prevQueue) => {
                return prevQueue.map((queuedUpload) => {
                    if (queuedUpload.id === upload.id) {
                        return {
                            ...queuedUpload,
                            state: 'creatingMultipartUpload',
                        };
                    }

                    return queuedUpload;
                });
            });

            upload.onStateChange('creatingMultipartUpload');

            try {
                const response = (
                    await appServer.request<InitiateMultipartUploadResponse>(
                        initiateMultipartUpload(
                            upload.routePrefix,
                            upload.initiateData,
                        ),
                    )
                ).data;

                const finalUploadUrl = buildUploadUrl({
                    bucket: response.bucket,
                    key: response.key,
                });

                setUploadQueue((prevQueue) => {
                    return prevQueue.map((queuedUpload) => {
                        if (queuedUpload.id === upload.id) {
                            return {
                                ...queuedUpload,
                                state: 'uploading',
                                completePresignedUrl:
                                    response.completePresignedUrl,
                                abortPresignedUrl: response.abortPresignedUrl,
                                uploadUrl: finalUploadUrl,
                                aws: {
                                    key: response.key,
                                    bucket: response.bucket,
                                    uploadId: response.uploadId,
                                },
                            };
                        }

                        return queuedUpload;
                    });
                });

                upload.onStateChange('uploading');
                upload.onUploadUrl(finalUploadUrl);

                logger.debug('[multipart-upload] opened upload on aws', {
                    uploadId: upload.id,
                    inputFileSource: upload.inputFileSource,
                });
            } catch (error) {
                captureException(
                    '[multipart-upload] failed to initiate multipart upload on AWS.',
                    {
                        error,
                        uploadId: upload.id,
                        inputFileSource: upload.inputFileSource,
                    },
                );

                abortUploads([upload.id], 'failedToInitiateUpload');
            } finally {
                currentlyPreparedUploads.current.delete(upload.id);
            }
        },
        [abortUploads, setUploadQueue],
    );

    const dequeueUpload = useCallback(
        (uploadId: string) => {
            abortUploads([uploadId], 'manualRemoval');
        },
        [abortUploads],
    );

    const abortFailedUploads = useCallback(() => {
        const failedUploadIds = Object.entries(allChunksPerUpload.current)
            .map(([uploadId, chunks]) => {
                if (chunks && chunks.some(hasChunkUploadFailed)) {
                    return uploadId;
                }

                return false;
            })
            .filter(Boolean);

        if (!failedUploadIds.length) {
            return;
        }

        abortUploads(failedUploadIds, 'chunkUploadFailed');
    }, [abortUploads]);

    const reportProgress = useThrottledCallback(
        (upload: ReadyUpload, state: Upload['state'] = 'uploading') => {
            const allChunks = allChunksPerUpload.current[upload.id];
            if (!allChunks) {
                return;
            }

            const { total, loaded } = allChunks.reduce(
                (acc, chunk) => {
                    const totalSize = chunk.total ?? chunk.chunk.size;
                    acc.total += totalSize;
                    const loaded =
                        chunk.loaded ??
                        (chunk.state === 'done' ? totalSize : 0); // support envs where `onUploadProgress` doesn't work
                    acc.loaded += loaded;
                    return acc;
                },
                { total: 0, loaded: 0 },
            );

            upload.onProgress(loaded / total, state);
        },
        100,
    );

    const uploadNextChunk = useCallback(
        async (upload: ReadyUpload, chunkForUpload: ReadyUploadChunk) => {
            chunkForUpload.state = 'uploading';
            try {
                const partNumber = chunkIndexToPartNumber(
                    chunkForUpload.chunkIndex,
                );

                const response = await uploadPart({
                    uploadUrl: chunkForUpload.presignedUrl,
                    data: chunkForUpload.chunk,
                    abortSignal: upload.abortSignal,
                    onUploadProgress: (progressEvent) => {
                        if (!progressEvent.total) {
                            return;
                        }

                        chunkForUpload.loaded = progressEvent.loaded;
                        chunkForUpload.total = progressEvent.total;

                        reportProgress(upload);
                    },
                });
                chunkForUpload.state = 'done';
                reportProgress(upload);
                reportProgress.flush();

                // No need for rerender on these, so we can mutate them
                upload.uploadedChunksCount += 1;
                upload.parts.push({
                    etag: response.headers['etag'] as string,
                    partNumber,
                });

                const areAllChunksUploaded =
                    upload.uploadedChunksCount === upload.allChunksCount;

                if (areAllChunksUploaded) {
                    upload.onProgress(1, 'finishedWithCurrentChunks');

                    setUploadQueue((prevQueue) => {
                        return prevQueue.map((queuedUpload) => {
                            if (queuedUpload.id === upload.id) {
                                return {
                                    ...upload,
                                    state: 'finishedWithCurrentChunks',
                                };
                            }

                            return queuedUpload;
                        });
                    });
                    upload.onStateChange('finishedWithCurrentChunks');
                }

                logger.debug('[multipart-upload] uploaded chunk', {
                    uploadId: upload.id,
                    inputFileSource: upload.inputFileSource,
                    chunkNumber: chunkForUpload.chunkIndex,
                });
            } catch (error: any) {
                if (error?.message === 'canceled') {
                    abortUploads([upload.id], 'manualRemoval');

                    logger.debug(
                        '[multipart-upload] canceled ongoing chunk upload',
                        {
                            uploadId: upload.id,
                            inputFileSource: upload.inputFileSource,
                            chunkNumber: chunkForUpload.chunkIndex,
                        },
                    );

                    return;
                }

                logger.warn('[multipart-upload] failed to upload chunk', {
                    uploadId: upload.id,
                    inputFileSource: upload.inputFileSource,
                    chunkNumber: chunkForUpload.chunkIndex,
                    attempt: chunkForUpload.attempt,
                    error,
                });

                chunkForUpload.state = 'idle';
                chunkForUpload.attempt = chunkForUpload.attempt + 1;
            } finally {
                parallelUploadingChunks.current.delete(chunkForUpload);
                setShouldTryChunkUpload((prev) => prev + 1);
            }
        },
        [abortUploads, reportProgress, setShouldTryChunkUpload, setUploadQueue],
    );

    const uploadNextChunks = useCallback(() => {
        void abortFailedUploads();

        const currentUpload = uploadQueue[0];

        if (!currentUpload || !isReadyUpload(currentUpload)) {
            return;
        }

        // Fetch presigned URLs lazily to avoid stockpiling URLs that expire
        // before use. Refill when the buffer of ready-to-upload chunks runs low.
        if (!fetchingPresignedUrls.current) {
            const allChunks =
                allChunksPerUpload.current[currentUpload.id] ?? [];
            const eligibleChunksCount = allChunks.filter(
                isChunkEligibleForUpload,
            ).length;

            if (eligibleChunksCount < PRESIGNED_URL_REFILL_THRESHOLD) {
                const chunksForUploadUrl = allChunks
                    .filter(doesChunkNeedPresignedUrl)
                    .slice(0, PRESIGNED_URL_BATCH_SIZE);

                if (chunksForUploadUrl.length) {
                    void fetchPresignedUrlForChunks(
                        chunksForUploadUrl,
                        currentUpload,
                    );
                }
            }
        }

        const chunksForUpload =
            allChunksPerUpload.current[currentUpload.id]?.filter(
                isChunkEligibleForUpload,
            ) ?? [];

        while (
            chunksForUpload.length !== 0 &&
            parallelUploadingChunks.current.size < MAX_PARALLEL_UPLOADS
        ) {
            const nextChunk = chunksForUpload.shift();

            if (!nextChunk) {
                break;
            }

            parallelUploadingChunks.current.add(nextChunk);
            void uploadNextChunk(currentUpload, nextChunk);
        }
    }, [
        abortFailedUploads,
        uploadQueue,
        fetchPresignedUrlForChunks,
        uploadNextChunk,
    ]);

    // Create the multipart upload to prepare for uploading or to conclude the upload
    useEffect(() => {
        const uploadsWithoutUrl = uploadQueue.filter(
            (upload) => upload.state === 'init',
        );

        uploadsWithoutUrl.forEach((upload) => {
            void prepareMultipartUpload(upload);
        });

        const uploadsToConclude = uploadQueue.filter(isUploadReadyToConclude);

        uploadsToConclude.forEach((upload) => {
            void concludeMultipartUpload(upload);
        });
    }, [concludeMultipartUpload, prepareMultipartUpload, uploadQueue]);

    // Trigger the chunk upload process on changes to chunk collection
    useEffect(() => {
        uploadNextChunks();
    }, [shouldTryChunkUpload, uploadNextChunks]);

    // Trigger the chunk upload process on changes to upload queue
    useEffect(() => {
        if (uploadQueue.length) {
            void uploadNextChunks();
        }
    }, [uploadNextChunks, uploadQueue]);

    const getNumberOfParallelUploads = useCallback(() => {
        return parallelUploadingChunks.current.size;
    }, []);

    return {
        addChunks,
        enqueueUpload,
        dequeueUpload,
        uploadQueue,
        concludeUpload: concludeMultipartUpload,
        getNumberOfParallelUploads,
    };
};
